/**
 * inplace/editPathMap.ts — DOM-node ↔ bodyHtml source map (ADR-002 §2/§3).
 *
 * The renderer (thread-api `deck_io/renderer.py` :: `annotate_edit_paths`)
 * stamps every element in a slide's `bodyHtml` at render time with:
 *   - `data-edit-path` : a stable structural address, `tag[nthOfType]` segments
 *     joined by `/` from the body root (one path per element). Stable across
 *     re-renders of unchanged structure.
 *   - `data-edit-kind`  : `"text"` (an editable leaf text run — heading,
 *     paragraph, list item, caption) | `"block"` (a draggable container).
 *
 * These attrs are **render-time only** — the stored `bodyHtml` (the source of
 * truth in `deck_json`) has none of them. So these helpers resolve a path
 * against the *clean* `bodyHtml`, mutate it, and re-serialize clean HTML; the
 * renderer re-annotates on the next render. The path algorithm here MUST
 * mirror `renderer.py` exactly (same author keeps them in lockstep — ADR-002
 * §3 "the renderer's path encoding and this resolver MUST be the same
 * algorithm").
 *
 * Path encoding (mirror of `renderer.annotate_edit_paths`):
 *   - Segments are `tagName[nthOfType]`, joined by `/`. `tagName` is
 *     lowercased; `nthOfType` is the 0-based index among same-tag siblings
 *     under the same parent.
 *   - The fragment's top-level elements are addressed as direct children of an
 *     *implicit root*. When we parse `<body>${bodyHtml}</body>`, `doc.body`
 *     IS that implicit root, so `doc.body`'s element children are the
 *     fragment's top-level elements (first top-level `<div>` → `div[0]`).
 */

import type { BlockMutation } from "./useBlockDnd";

export const EDIT_PATH_ATTR = "data-edit-path";
export const EDIT_KIND_ATTR = "data-edit-kind";

/** Editable leaf text run. */
export const EDIT_KIND_TEXT = "text";
/** Draggable container block. */
export const EDIT_KIND_BLOCK = "block";

/** Parse a single `tag[n]` segment → `[tag, n]`, or null if malformed. */
function parseSegment(segment: string): [string, number] | null {
  const m = /^([^[]+)\[(\d+)\]$/.exec(segment);
  if (!m) return null;
  const [, tag, index] = m;
  if (tag === undefined || index === undefined) return null;
  return [tag.toLowerCase(), Number(index)];
}

/**
 * Pick the `n`-th element child of `parent` whose lowercased tagName matches
 * `tag` (0-based, counting only same-tag siblings — mirrors the renderer's
 * `nthOfType`). Returns null if there are fewer than `n + 1` matches.
 */
function nthOfType(
  parent: ParentNode,
  tag: string,
  n: number,
): Element | null {
  let seen = 0;
  for (const child of Array.from(parent.children)) {
    if (child.tagName.toLowerCase() === tag) {
      if (seen === n) return child;
      seen += 1;
    }
  }
  return null;
}

/**
 * Resolve an `editPath` to its element within `root` (a parsed `bodyHtml`
 * fragment). Returns null if the path doesn't resolve (stale path after a
 * structural change). Mirror of the renderer's path emission.
 */
export function resolveEditPath(
  root: ParentNode,
  editPath: string,
): Element | null {
  if (!editPath) return null;
  let current: ParentNode | null = root;
  for (const segment of editPath.split("/")) {
    if (!current) return null;
    const parsed = parseSegment(segment);
    if (!parsed) return null;
    const [tag, n] = parsed;
    const next: Element | null = nthOfType(current, tag, n);
    if (!next) return null;
    current = next;
  }
  // `current` is the root only when editPath was empty (handled above), so a
  // successful walk always lands on an Element.
  return current === root ? null : (current as Element);
}

/** Parse a `bodyHtml` fragment into a document whose `body` is the impl. root. */
function parseFragment(bodyHtml: string): Document {
  return new DOMParser().parseFromString(
    `<body>${bodyHtml}</body>`,
    "text/html",
  );
}

function textNodesUnder(node: Node): Text[] {
  const textNodes: Text[] = [];
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === 3) {
      textNodes.push(child as Text);
    } else {
      textNodes.push(...textNodesUnder(child));
    }
  }
  return textNodes;
}

function changedTextRange(
  oldText: string,
  newText: string,
): { start: number; oldEnd: number; newEnd: number } {
  let start = 0;
  while (
    start < oldText.length &&
    start < newText.length &&
    oldText[start] === newText[start]
  ) {
    start += 1;
  }

  let oldEnd = oldText.length;
  let newEnd = newText.length;
  while (
    oldEnd > start &&
    newEnd > start &&
    oldText[oldEnd - 1] === newText[newEnd - 1]
  ) {
    oldEnd -= 1;
    newEnd -= 1;
  }

  return { start, oldEnd, newEnd };
}

function insertTextAtOffset(node: Element, offset: number, text: string): void {
  if (!text) return;
  const textNodes = textNodesUnder(node);
  if (textNodes.length === 0) {
    node.appendChild(node.ownerDocument.createTextNode(text));
    return;
  }

  let cursor = 0;
  for (const textNode of textNodes) {
    const value = textNode.data;
    const end = cursor + value.length;
    if (offset <= end) {
      const local = Math.max(offset - cursor, 0);
      textNode.data = value.slice(0, local) + text + value.slice(local);
      return;
    }
    cursor = end;
  }

  textNodes[textNodes.length - 1]!.data += text;
}

function replaceTextRange(
  node: Element,
  start: number,
  end: number,
  replacement: string,
): void {
  if (start === end) {
    insertTextAtOffset(node, start, replacement);
    return;
  }

  let inserted = false;
  let cursor = 0;
  for (const textNode of textNodesUnder(node)) {
    const value = textNode.data;
    const nodeStart = cursor;
    const nodeEnd = cursor + value.length;
    cursor = nodeEnd;
    if (nodeEnd <= start || nodeStart >= end) continue;

    const localStart = Math.max(start - nodeStart, 0);
    const localEnd = Math.min(end - nodeStart, value.length);
    textNode.data =
      value.slice(0, localStart) +
      (inserted ? "" : replacement) +
      value.slice(localEnd);
    inserted = true;
  }

  if (!inserted) insertTextAtOffset(node, start, replacement);
}

function replaceTextPreservingChildren(node: Element, newText: string): void {
  if (node.children.length === 0) {
    node.textContent = newText;
    return;
  }

  const oldText = node.textContent ?? "";
  const { start, oldEnd, newEnd } = changedTextRange(oldText, newText);
  replaceTextRange(node, start, oldEnd, newText.slice(start, newEnd));
}

/**
 * Phase C — replace the text content of the node at `editPath` with
 * `newText`, preserving the node's own tag + attributes and any inline
 * children already present in stored `bodyHtml`. Returns the new `bodyHtml`.
 * No-op (returns the input) if the path doesn't resolve.
 */
export function editPathSplice(
  bodyHtml: string,
  editPath: string,
  newText: string,
): string {
  const doc = parseFragment(bodyHtml);
  const node = resolveEditPath(doc.body, editPath);
  if (!node) return bodyHtml;
  replaceTextPreservingChildren(node, newText);
  return doc.body.innerHTML;
}

/** True iff `ancestor` contains `descendant` (or they are the same node). */
function containsOrEqual(ancestor: Element, descendant: Element): boolean {
  return ancestor === descendant || ancestor.contains(descendant);
}

/**
 * Phase D — apply a block `move`/`insert` mutation (expressed in edit-paths)
 * to `bodyHtml` and return the new `bodyHtml`. No-op if paths don't resolve.
 */
export function applyBlockMutation(
  bodyHtml: string,
  mutation: BlockMutation,
): string {
  const doc = parseFragment(bodyHtml);

  if (mutation.kind === "move") {
    if (mutation.sourcePath === mutation.targetPath) return bodyHtml;
    const source = resolveEditPath(doc.body, mutation.sourcePath);
    const target = resolveEditPath(doc.body, mutation.targetPath);
    if (!source || !target) return bodyHtml;
    // Refuse to move a block into/around its own descendant (would detach the
    // target, or create a cycle for "into").
    if (containsOrEqual(source, target)) return bodyHtml;

    source.remove();
    insertRelative(source, target, mutation.position);
    return doc.body.innerHTML;
  }

  // kind === "insert": deferred for v1. Resolving the palette `templateId`
  // to a concrete subtree is the caller's job (block palette → HTML), and is
  // not wired yet. Handle gracefully as a no-op so a stray insert can't
  // corrupt the body.
  // TODO(ADR-002 Phase D): materialise `mutation.templateId` → element and
  //   insert it relative to `mutation.targetPath` via `insertRelative`.
  return bodyHtml;
}

/** Insert `node` before/after/into `target` per `position`. */
function insertRelative(
  node: Element,
  target: Element,
  position: BlockMutation["position"],
): void {
  if (position === "into") {
    target.appendChild(node);
    return;
  }
  const parent = target.parentNode;
  if (!parent) {
    // Target detached (shouldn't happen for a resolved path); append into it.
    target.appendChild(node);
    return;
  }
  if (position === "before") {
    parent.insertBefore(node, target);
  } else {
    // "after"
    parent.insertBefore(node, target.nextSibling);
  }
}
